import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { PLAN_ORDER } from '../../../config/planFeatures';

const PricingTable = ({ pricingTiers, onEdit, onDelete, onReorder, onToggleActive, onCreate }) => {
  const formatPrice = (price) => {
    if (price === null || price === undefined || price === '') return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    })?.format(price);
  };

  const handleMove = (index, direction) => {
    const newTiers = [...pricingTiers];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newTiers?.length) return;
    [newTiers[index], newTiers[targetIndex]] = [newTiers?.[targetIndex], newTiers?.[index]];
    onReorder?.(newTiers);
  };

  return (
    <div className="space-y-4">
      {onCreate && (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-medium text-foreground">Add a custom pricing plan</p>
            <p className="text-sm text-muted-foreground">
              Create an additional plan for internal configuration without changing the four subscriber tiers.
            </p>
          </div>
          <Button variant="default" size="sm" onClick={onCreate}>
            <Icon name="Plus" size={16} className="mr-1.5" />
            Create A Plan
          </Button>
        </div>
      )}
      {pricingTiers?.length > 1 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-primary/5 border border-primary/20 rounded-lg text-sm text-primary">
          <Icon name="ArrowLeftRight" size={16} />
          <span>Use the <strong>↑ ↓</strong> arrows to set the left-to-right display order of plans on the pricing page.</span>
        </div>
      )}
      <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-20">Order</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Plan Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Monthly</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Annual</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Limits</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Features</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {pricingTiers?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No pricing plans configured yet</p>
                      <p className="text-sm text-muted-foreground mt-1">The approved pricing catalog contains four fixed plans.</p>
                  </td>
                </tr>
              ) : (
                pricingTiers?.map((tier, index) => (
                  <tr key={tier?.id} className={`hover:bg-muted/30 transition-colors ${!tier?.isActive ? 'opacity-60' : ''}`}>
                    {/* Order controls */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <span className="w-6 text-center text-sm font-semibold text-muted-foreground">{index + 1}</span>
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => handleMove(index, 'up')}
                            disabled={index === 0}
                            title="Move left"
                            className={`p-0.5 rounded transition-colors ${index === 0 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-primary hover:bg-primary/10'}`}
                          >
                            <Icon name="ChevronUp" size={14} />
                          </button>
                          <button
                            onClick={() => handleMove(index, 'down')}
                            disabled={index === pricingTiers?.length - 1}
                            title="Move right"
                            className={`p-0.5 rounded transition-colors ${index === pricingTiers?.length - 1 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-primary hover:bg-primary/10'}`}
                          >
                            <Icon name="ChevronDown" size={14} />
                          </button>
                        </div>
                      </div>
                    </td>

                    {/* Plan Name */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{tier?.tierName}</span>
                          {tier?.isPopular && (
                            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">Popular</span>
                          )}
                        </div>
                        {tier?.badgeText && (
                          <span className="text-xs text-muted-foreground">{tier?.badgeText}</span>
                        )}
                        {tier?.ctaText && tier?.ctaText !== 'Get Started' && (
                          <span className="text-xs text-muted-foreground italic">CTA: {tier?.ctaText}</span>
                        )}
                      </div>
                    </td>

                    {/* Monthly Price */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-base font-semibold text-foreground">{formatPrice(tier?.price)}</span>
                      <span className="text-xs text-muted-foreground ml-1">/mo</span>
                    </td>

                    {/* Annual Price */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tier?.annualPrice != null ? (
                        <>
                          <span className="text-base font-semibold text-foreground">
                            {formatPrice(tier?.annualMonthlyPrice ?? Number(tier.annualPrice) / 12)}
                          </span>
                          <span className="text-xs text-muted-foreground ml-1">/mo</span>
                          <span className="block text-xs text-muted-foreground">{formatPrice(tier?.annualPrice)}/yr</span>
                        </>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </td>

                    {/* Limits */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs space-y-0.5">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Icon name="FileText" size={11} />
                          <span>{tier?.maxForms === -1 ? 'Unlimited forms' : `${tier?.maxForms ?? '—'} forms`}</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Icon name="Inbox" size={11} />
                          <span>{tier?.maxSubmissions === -1 ? 'Unlimited subs' : `${tier?.maxSubmissions ?? '—'}/mo`}</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Icon name="Users" size={11} />
                          <span>{tier?.maxTeamMembers === -1 ? 'Unlimited seats' : `${tier?.maxTeamMembers ?? '—'} seats`}</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Icon name="HardDrive" size={11} />
                          <span>{tier?.maxStorageMb === -1 ? 'Unlimited storage' : tier?.maxStorageMb != null ? `${tier.maxStorageMb >= 1024 ? tier.maxStorageMb / 1024 : tier.maxStorageMb} ${tier.maxStorageMb >= 1024 ? 'GB' : 'MB'}` : '—'}</span>
                        </div>
                      </div>
                    </td>

                    {/* Features */}
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {Array.isArray(tier?.features)
                          ? tier?.features?.slice(0, 3)?.map((feature, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-muted text-xs text-foreground rounded">
                                {typeof feature === 'object' && feature !== null
                                  ? `${feature?.text ?? ''}: ${feature?.value ?? ''}`
                                  : String(feature)}
                              </span>
                            ))
                          : null}
                        {Array.isArray(tier?.features) && tier?.features?.length > 3 && (
                          <span className="px-2 py-0.5 text-xs text-muted-foreground">+{tier?.features?.length - 3} more</span>
                        )}
                        {(!tier?.features || (Array.isArray(tier?.features) && tier?.features?.length === 0)) && (
                          <span className="text-xs text-muted-foreground italic">No features</span>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => onToggleActive?.(tier)}
                        title={tier?.isActive ? 'Click to deactivate' : 'Click to activate'}
                        className={`px-2 py-1 text-xs rounded-full font-medium transition-colors cursor-pointer ${
                          tier?.isActive
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' :'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {tier?.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {(() => {
                        const isCanonical = PLAN_ORDER.includes(tier?.planKey);
                        return (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(tier)}
                          className="text-primary hover:text-primary/80"
                          title="Edit plan"
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(tier?.id)}
                          disabled={isCanonical}
                          className="text-destructive hover:text-destructive/80"
                          title={isCanonical ? 'Canonical plans cannot be deleted' : 'Delete plan'}
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PricingTable;
